import React from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { useTier } from '../context/TierContext';
import { ScoreCard, LoadingState, ErrorState, EmptyState } from '../components/primitives';
import { fmtInt, timeAgo } from '../lib/format';

export default function Support() {
  const { tier } = useTier();
  const { data, loading, error } = useApiData(() => api.getSupport(tier), [tier]);

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState message={error || 'No data available.'} />;

  const { openTickets, unreadFeedback, byClient } = data.data;
  const clientsWithTickets = byClient.filter((c) => c.openTickets > 0).length;

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Support &amp; feedback</div>
          <h1 className="title">
            <span className="serif">Open tickets across every client</span>
          </h1>
          <p className="subtitle">
            The oldest open tickets float to the top. Resolve from each client's own admin portal.
          </p>
        </div>
      </div>

      <div className="scorecard">
        <ScoreCard label="Open tickets" value={fmtInt(openTickets.length)} feature sub="Across all clients." />
        <ScoreCard label="Unread feedback" value={fmtInt(unreadFeedback)} feature sub="Awaiting a first read." />
        <ScoreCard label="Clients w/ tickets" value={fmtInt(clientsWithTickets)} feature sub={`of ${byClient.length} clients`} />
      </div>

      <section className="section">
        <div className="section-head">
          <div>
            <h3>Open tickets · oldest first</h3>
            <p className="desc">The queue that needs attention.</p>
          </div>
        </div>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Ticket</th>
                <th style={{ width: 180 }}>Client</th>
                <th style={{ width: 110 }}>Severity</th>
                <th style={{ width: 160 }}>Requester</th>
                <th style={{ width: 130 }}>Opened</th>
              </tr>
            </thead>
            <tbody>
              {openTickets.map((ticket, i) => (
                <tr key={ticket.ticketId}>
                  <td>
                    <span className="mono" style={{ color: 'var(--ink-muted)' }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </td>
                  <td>{ticket.title}</td>
                  <td>
                    {ticket.tenantId ? (
                      <Link to={`/clients/${ticket.tenantId}`} className="mono" style={{ color: 'var(--ink-muted)', textDecoration: 'none' }}>
                        {ticket.displayName}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <span className={'chip ' + (ticket.severity === 'high' ? 'neg' : ticket.severity === 'medium' ? 'warn' : '')}>
                      {ticket.severity}
                    </span>
                  </td>
                  <td className="mono" style={{ color: 'var(--ink-muted)' }}>
                    {ticket.name || ticket.email || '—'}
                  </td>
                  <td>{timeAgo(ticket.createdAt)}</td>
                </tr>
              ))}
              {openTickets.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <EmptyState>
                      <b>No open tickets.</b> Everything is resolved.
                    </EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <div>
            <h3>By client</h3>
            <p className="desc">Ticket and feedback volume per client.</p>
          </div>
        </div>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th style={{ width: 100 }}>Open</th>
                <th style={{ width: 100 }}>Total</th>
                <th style={{ width: 140 }}>Unread feedback</th>
                <th style={{ width: 160 }}>Oldest open</th>
              </tr>
            </thead>
            <tbody>
              {byClient.map((c) => (
                <tr key={c.tenantId}>
                  <td>
                    <Link to={`/clients/${c.tenantId}`} style={{ color: 'var(--ink)', fontWeight: 500, textDecoration: 'none' }}>
                      {c.displayName}
                    </Link>
                  </td>
                  <td>
                    <span className={'metric-num' + (c.openTickets > 0 ? ' warn' : '')}>{c.openTickets}</span>
                  </td>
                  <td>
                    <span className="metric-num">{c.totalTickets}</span>
                  </td>
                  <td>
                    <span className="metric-num">{c.unreadFeedback}</span>
                  </td>
                  <td>{c.oldestOpenTicketAt ? timeAgo(c.oldestOpenTicketAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
